import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, markReconcileHandledByList, reconcileRequiredCount } from './client'

// --- Types ---

export interface PromotionEvent {
  id: number
  ruleId: number
  keywordId: number | null
  searchTerm: string
  status: 'pending' | 'approved' | 'rejected' | 'applied' | 'auto_applied' | 'rolled_back'
  metrics: {
    clicks: number
    impressions: number
    spend: number
    sales: number
    orders: number
    acos: number
    cpc: number
    ctr: number
    roas: number
    campaignId: number
    adGroupId: number
  }
  reviewedBy: number | null
  reviewedAt: string | null
  appliedAt: string | null
  createdAt: string
  ruleName: string
  ruleFromStage: string
  ruleToStage: string
  // v24: Bid-Display
  suggestedBid: string | null
  bidAtEventTime: string | null
  currentBid: string | null
  bidDelta: number | null
}

// v31: strukturierte Conditions (passend zu describeConditions + Backend-Conditions)
export interface RuleConditions {
  minClicks?: number
  minOrders?: number
  maxAcos?: number
  minAcos?: number
  minSpend?: number
  // Power-User-Felder (selten benoetigt, aber Backend unterstuetzt)
  minImpressions?: number
  minSales?: number
  minCtr?: number
  minRoas?: number
  maxCpc?: number
  // Erlaubt Backwards-Compat fuer komplexe and/or-Conditions oder unbekannte Felder
  [key: string]: unknown
}

export type RuleStage =
  | 'auto'
  | 'broad'
  | 'phrase'
  | 'exact'
  | 'negative'
  | 'bid_increase'
  | 'bid_decrease'
  | 'any'

export interface PromotionRule {
  id: number
  name: string
  description: string | null
  fromStage: string
  toStage: string
  conditions: RuleConditions
  autoApply: number
  cooldownMinutes: number
  maxPerDay: number
  priority: number
  isActive: number
  createdAt: string
  updatedAt: string
}

export interface CreateRuleInput {
  name: string
  description?: string | null
  fromStage: RuleStage
  toStage: RuleStage
  conditions: RuleConditions
  priority?: number
  isActive?: number
  autoApply?: number
  maxPerDay?: number
  cooldownMinutes?: number
}

export interface UpdateRuleInput {
  id: number
  name?: string
  description?: string | null
  fromStage?: RuleStage
  toStage?: RuleStage
  priority?: number
  isActive?: number
  autoApply?: number
  maxPerDay?: number
  cooldownMinutes?: number
  conditions?: RuleConditions
}

export interface PromotionStats {
  pending: number
  approved: number
  rejected: number
  applied: number
  autoApplied: number
  last7d: number
  last30d: number
}

export interface SearchTermAggregated {
  campaignId: number
  adGroupId: number
  keywordId: number | null
  customerSearchTerm: string
  clicks: number
  impressions: number
  spend: number
  sales: number
  orders: number
  acos: number
  cpc: number
  ctr: number
  roas: number
  category: 'winner' | 'potential' | 'loser' | 'insufficient_data'
}

export interface CleanupKeyword {
  id: number
  text: string
  matchType: string
  adGroupId: number
  createdAt: string
  daysSinceCreation: number
}

/**
 * CLEANUP-SICHT: ein offener Pruefmarker aus `cleanup_events`, gelesen von
 * `listOpenCleanupReconciles` (backend cleanup.ts). Der durable Gegenpol zum
 * fluechtigen 502-Banner: das Banner meldet den gerade gelaufenen Batch, diese
 * Liste den Bestand, der noch offen ist — auch nach einem Reload.
 *
 * `keywordText` ist nullable, weil der Leser per LEFT JOIN liest: ein
 * zwischenzeitlich geloeschtes Keyword darf den Marker nicht verschwinden
 * lassen, die Pruefpflicht haengt am Marker.
 */
export interface CleanupOpenReconcile {
  id: number
  keywordId: number
  keywordText: string | null
  reason: string
  createdAt: string
}

/**
 * CLEANUP-SICHT (Whole-Branch-Fix C3): die Antwort von `GET /promotions/cleanup/`.
 *
 * `openReconciles` ist der ANGEZEIGTE AUSSCHNITT (die neuesten 100 Marker) —
 * nicht der Bestand. Diese Verwechslung war der Befund: ab Marker 101 blieb ein
 * aelteres betroffenes Keyword nach einem Reload ungesperrt. Deshalb zwei
 * weitere Felder, die der Ausschnitt nicht liefern kann:
 *  - `openReconcilesTotal` — Zahl ALLER offenen Marker, fuer die ehrliche
 *    Beschriftung des Abschnitts.
 *  - `openReconcileKeywordIds` — ALLE Keywords mit offenem Marker, unbegrenzt.
 *    Das ist die SPERRQUELLE; sie kostet nur eine Zahl je Vorfall.
 */
export interface CleanupListResponse {
  keywords: CleanupKeyword[]
  total: number
  openReconciles: CleanupOpenReconcile[]
  openReconcilesTotal: number
  openReconcileKeywordIds: number[]
}

/** Einzelausgang eines Cleanup-Laufs (backend `CleanupItemResult`). */
export interface CleanupItemResult {
  keywordId: number
  ok: boolean
  error?: string
  reconcileRequired?: boolean
  policyDenial?: boolean
}

export interface CleanupExecuteResult {
  archived: number
  errors: string[]
  results: CleanupItemResult[]
}

export type CleanupReconcileOutcome = 'archived' | 'not_archived'

/**
 * `auditNote` ist gesetzt, wenn die Quittierung durabel gespeichert wurde, ihre
 * Audit-Spur aber ausgefallen ist (backend Fix-Runde 1). Kein Fehlerfall, aber
 * die einzige Stelle, an der dieser Ausfall ueberhaupt auftaucht — ein Feld
 * ohne Konsument waere hier genau die Fehlerklasse, gegen die dieses Modul
 * gebaut ist. Deshalb optional im Typ und pflichtig in der Darstellung.
 */
export interface CleanupResolveResult {
  resolved: boolean
  keywordUpdated: boolean
  /**
   * Warum die lokale Buchung nichts zu tun fand (Whole-Branch-Fix I1). Gesetzt
   * genau dann, wenn `keywordUpdated: false` bei outcome='archived' — der
   * Boolean allein sagt dem Operator nicht, ob das harmlos ist. Ein
   * Zustandskonflikt kommt gar nicht hier an, sondern als 409.
   */
  localBookingNote?: string
  auditNote?: string
}

/**
 * CLEANUP-SICHT (Fix-Runde 1, Finding 1+2): EIN Lauf, drei Ausgangsklassen —
 * getrennt nach dem, was der Operator daraus tun darf.
 *
 * Der Unterschied ist nicht kosmetisch: ein deterministischer Fehlschlag ist
 * beweisbar schreiblos und gefahrlos wiederholbar, ein ungeklaerter Ausgang
 * NICHT. Wer beide gleich behandelt, laedt genau den zweiten Dispatch ein, vor
 * dem das Banner warnt.
 */
export interface CleanupRunOutcome {
  /** Bestaetigt bei Amazon archiviert — der Zaehler des Backends, nicht nachgerechnet. */
  archived: number
  /**
   * Die IDs zu diesem Zaehler: bestaetigt archiviert, also ERLEDIGT. Eigene
   * Klasse neben `unresolvedKeywordIds`, weil sich beide verschieden aufloesen —
   * ein ungeklaerter Ausgang durch eine Quittierung, ein Erfolg gar nicht mehr.
   * `notRepeatableKeywordIds` fuehrt beide zusammen und kann sie nicht trennen.
   */
  archivedKeywordIds: number[]
  /** Fehlschlaege mit BEKANNTEM Amazon-Ausgang, im Wortlaut von `errors[]`. */
  deterministicErrors: string[]
  /** Ungeklaerter Ausgang — gehoert in den Marker-Abschnitt, nicht in die Arbeitsliste. */
  unresolvedKeywordIds: number[]
  /**
   * Die MELDUNGEN der ungeklaerten Eintraege, im Wortlaut von `errors[]`
   * (Whole-Branch-Fix C1).
   *
   * Bis hierhin ueberlebten nur die IDs, und genau daran ging der wichtigste
   * Satz des Backends verloren: der Hinweis, dass der durable Pruefmarker NICHT
   * geschrieben werden konnte (oder nur seine Audit-Spur fehlt). Ohne ihn
   * forderte die Ansicht generisch zum Quittieren auf, waehrend es nach einem
   * Reload weder Marker noch Sperre gab — dasselbe Keyword war erneut
   * dispatchbar. Der best-effort-Pfad des Backends verliert seine ganze Wirkung,
   * wenn seine Meldung die Schichtgrenze nicht ueberlebt.
   */
  unresolvedErrors: string[]
  /** Erfolge + Ungeklaerte: alles, was NICHT gefahrlos wiederholbar ist. */
  notRepeatableKeywordIds: number[]
}

/** Meldungsform von `errors[]` im Backend (cleanup.ts) — 1:1, nicht neu erfunden. */
function itemMeldung(item: CleanupItemResult): string {
  return `Keyword ${item.keywordId}: ${item.error ?? 'Fehlschlag ohne Meldung'}`
}

export function describeCleanupRun(result: CleanupExecuteResult): CleanupRunOutcome {
  const archivedKeywordIds: number[] = []
  const deterministicErrors: string[] = []
  const unresolvedKeywordIds: number[] = []
  const unresolvedErrors: string[] = []
  const notRepeatableKeywordIds: number[] = []

  // Klassifiziert wird ueber `results`, NICHT ueber `errors`: nur `results`
  // traegt den Diskriminator `reconcileRequired`. `errors[]` fuehrt beide
  // Klassen im selben String-Array und kann sie nicht trennen.
  for (const item of result.results) {
    if (item.ok) {
      archivedKeywordIds.push(item.keywordId)
      notRepeatableKeywordIds.push(item.keywordId)
      continue
    }
    if (item.reconcileRequired === true) {
      unresolvedKeywordIds.push(item.keywordId)
      // Wortlaut 1:1 wie `errors[]` im Backend — hier haengt der Marker-Ausfall
      // dran, und der ist die teuerste Nachricht dieses Moduls.
      unresolvedErrors.push(itemMeldung(item))
      notRepeatableKeywordIds.push(item.keywordId)
      continue
    }
    // Wortlaut 1:1 wie `errors[]` im Backend (cleanup.ts) — dieselbe Meldung
    // darf nicht je nach Antwortstatus anders aussehen.
    deterministicErrors.push(itemMeldung(item))
  }

  return {
    archived: result.archived,
    archivedKeywordIds,
    deterministicErrors,
    unresolvedKeywordIds,
    unresolvedErrors,
    notRepeatableKeywordIds,
  }
}

function isCleanupItemResult(entry: unknown): entry is CleanupItemResult {
  if (typeof entry !== 'object' || entry === null) return false
  const feld = entry as Record<string, unknown>
  return typeof feld['keywordId'] === 'number' && typeof feld['ok'] === 'boolean'
}

/**
 * Ergebnis der Rumpf-Lesung — drei Ausgaenge, weil drei verschiedene Handlungen
 * folgen:
 *  - `none`: kein auswertbarer FORM-1-502. Es bleibt beim blossen Banner.
 *  - `ok`: der Rumpf ist vollstaendig und in sich stimmig.
 *  - `inconsistent`: der Rumpf widerspricht sich oder ist unvollstaendig. Dann
 *    ist NICHT entscheidbar, welches Keyword welchen Ausgang hatte — der
 *    Aufrufer muss konservativ reagieren.
 */
export type CleanupReconcileReading =
  | { status: 'none' }
  | { status: 'ok'; run: CleanupExecuteResult }
  | { status: 'inconsistent'; reason: string }

/**
 * CLEANUP-SICHT (Fix-Runde 1, Finding 1 · Fix-Runde 2, WICHTIG 3): die
 * Nutzdaten AUS dem 502 — atomar validiert.
 *
 * Ein `RECONCILE_REQUIRED`-502 ist ein TEILergebnis, kein Fehlschlag: das
 * Backend haelt den vollen Batch unter `body.result` fest, und `ApiError` traegt
 * den geparsten Rumpf seit M9 genau dafuer mit. Wer nur die Zahl der
 * pruefbeduerftigen Eintraege liest, verwirft die bereits BEKANNTEN Ausgaenge
 * desselben Laufs.
 *
 * ABER: „defensiv" hiess in Fix-Runde 1 noch, fehlende Felder auf leere Werte
 * zu setzen und unlesbare Eintraege wegzufiltern. Das ist die FALSCHE Richtung.
 * Ein weggefiltertes Einzelergebnis nimmt seinem Keyword den Ausgang, und ohne
 * Ausgang gilt es beim Aufrufer implizit als gefahrlos wiederholbar — also
 * ausgerechnet als das Gegenteil dessen, was ein ungeklaerter Eintrag ist.
 * Deshalb wird jetzt ATOMAR validiert: Vollstaendigkeit gegen den abgesendeten
 * Batch und `archived === Anzahl der ok-Eintraege`. Bei jeder Abweichung ist
 * die GANZE Rueckmeldung unbrauchbar — fail-closed statt teilweise geglaubt.
 *
 * Nominal koennen die beiden Zahlen nicht auseinanderlaufen (das Backend
 * schreibt `archived++` und `results.push({ok:true})` in derselben Zeile,
 * cleanup.ts:545-546). Die Pruefung gilt dem nicht-nominalen Fall: einer
 * abweichenden Backend-Version, einem abgeschnittenen Rumpf, einem Proxy
 * dazwischen.
 */
export function readCleanupReconcileBody(err: unknown, dispatchedIds: number[]): CleanupReconcileReading {
  if (!(err instanceof ApiError)) return { status: 'none' }
  if (reconcileRequiredCount(err) === 0) return { status: 'none' }

  const result = err.body?.result
  if (typeof result !== 'object' || result === null) {
    return { status: 'inconsistent', reason: 'Der Lauf hat keine Einzelergebnisse mitgeliefert.' }
  }
  const roh = result as Record<string, unknown>

  const rohResults = roh['results']
  if (!Array.isArray(rohResults)) {
    return { status: 'inconsistent', reason: 'Die Einzelergebnisse des Laufs fehlen.' }
  }
  const eintraege: unknown[] = rohResults
  if (!eintraege.every(isCleanupItemResult)) {
    return { status: 'inconsistent', reason: 'Mindestens ein Einzelergebnis war nicht lesbar.' }
  }

  const archived = roh['archived']
  if (typeof archived !== 'number') {
    return { status: 'inconsistent', reason: 'Der Zähler der archivierten Keywords fehlt.' }
  }
  const bestaetigt = eintraege.filter(e => e.ok).length
  if (archived !== bestaetigt) {
    return {
      status: 'inconsistent',
      reason: `Der Zähler nennt ${archived} archivierte Keywords, die Einzelergebnisse ${bestaetigt}.`,
    }
  }

  const gemeldet = new Set(eintraege.map(e => e.keywordId))
  const fehlend = dispatchedIds.filter(id => !gemeldet.has(id))
  if (fehlend.length > 0) {
    return {
      status: 'inconsistent',
      reason: `Zu ${fehlend.length} abgesendeten Keywords fehlt jede Rückmeldung.`,
    }
  }

  return {
    status: 'ok',
    run: {
      archived,
      errors: Array.isArray(roh['errors']) ? roh['errors'].filter((e): e is string => typeof e === 'string') : [],
      results: eintraege,
    },
  }
}

export interface EventFilters {
  status?: string
  ruleId?: number
  dateFrom?: string
  dateTo?: string
  limit?: number
  offset?: number
}

// --- Hooks ---

export function usePromotionEvents(filters: EventFilters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.ruleId) params.set('ruleId', String(filters.ruleId))
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.offset) params.set('offset', String(filters.offset))

  const qs = params.toString()
  return useQuery({
    queryKey: ['promotionEvents', filters],
    queryFn: () => api.get<{ events: PromotionEvent[]; total: number; limit: number; offset: number }>(`/promotions/events/${qs ? `?${qs}` : ''}`),
    refetchInterval: filters.status === 'pending' || !filters.status ? 30_000 : false,
  })
}

export function usePromotionStats() {
  return useQuery({
    queryKey: ['promotionStats'],
    queryFn: () => api.get<{ stats: PromotionStats }>('/promotions/stats/'),
    refetchInterval: 30_000,
  })
}

export function usePromotionRules() {
  return useQuery({
    queryKey: ['promotionRules'],
    queryFn: () => api.get<{ rules: PromotionRule[] }>('/promotions/rules/'),
  })
}

export function useApproveEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post(`/promotions/events/${id}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promotionEvents'] })
      qc.invalidateQueries({ queryKey: ['promotionStats'] })
    },
  })
}

export function useRejectEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post(`/promotions/events/${id}/reject`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promotionEvents'] })
      qc.invalidateQueries({ queryKey: ['promotionStats'] })
    },
  })
}

export function useApplyEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post(`/promotions/events/${id}/apply`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promotionEvents'] })
      qc.invalidateQueries({ queryKey: ['promotionStats'] })
    },
  })
}

export function useBatchPromotionAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ action, ids }: { action: 'approve' | 'reject'; ids: number[] }) =>
      api.post('/promotions/events/batch/', { action, ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promotionEvents'] })
      qc.invalidateQueries({ queryKey: ['promotionStats'] })
    },
  })
}

export function useUpdateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: UpdateRuleInput) =>
      api.put<{ rule: PromotionRule }>(`/promotions/rules/${id}/`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promotionRules'] })
    },
  })
}

export function useCreateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateRuleInput) =>
      api.post<{ rule: PromotionRule }>('/promotions/rules/', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promotionRules'] })
    },
  })
}

export function useDeleteRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ deleted: boolean; ruleId: number; ruleName: string; affectedEvents: number }>(
        `/promotions/rules/${id}/`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promotionRules'] })
      qc.invalidateQueries({ queryKey: ['promotionEvents'] })
    },
  })
}

// Vorab-Check fuer Delete-Confirm: wieviele Events werden auf ruleId=null gesetzt?
export async function fetchRuleEventsCount(id: number): Promise<number> {
  const res = await api.get<{ ruleId: number; affectedEvents: number }>(
    `/promotions/rules/${id}/events-count/`,
  )
  return res.affectedEvents
}

export function useEvaluatePromotions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ eventsCreated: number; skippedDuplicate: number; skippedCooldown: number }>('/promotions/evaluate/'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promotionEvents'] })
      qc.invalidateQueries({ queryKey: ['promotionStats'] })
    },
  })
}

export function useSearchTerms(query: { days?: number; range?: { from: string; to: string }; campaignId?: number; category?: string; limit?: number; offset?: number } = {}) {
  const params = new URLSearchParams()
  if (query.range) {
    params.set('dateFrom', query.range.from)
    params.set('dateTo', query.range.to)
  } else if (query.days) {
    params.set('days', String(query.days))
  }
  if (query.campaignId) params.set('campaignId', String(query.campaignId))
  if (query.category) params.set('category', query.category)
  if (query.limit) params.set('limit', String(query.limit))
  if (query.offset) params.set('offset', String(query.offset))

  const qs = params.toString()
  return useQuery({
    queryKey: ['searchTerms', query.range?.from ?? null, query.range?.to ?? null, query.days ?? null, query.campaignId ?? null, query.category ?? null, query.limit ?? null, query.offset ?? null],
    queryFn: () => api.get<{ searchTerms: SearchTermAggregated[]; total: number }>(`/promotions/search-terms/${qs ? `?${qs}` : ''}`),
  })
}

export type CleanupKeywordsQuery = { daysThreshold?: number; limit?: number; offset?: number }

/**
 * Der Query-Schluessel dieser Abfrage — exportiert, weil die Ansicht ihn
 * BRAUCHT (Whole-Branch-Fix C2).
 *
 * Die Ansicht blockiert nach einer widerspruechlichen Antwort so lange, bis die
 * Pruefmarker neu geladen sind. Sie erkannte das bisher an einem Wechsel von
 * `dataUpdatedAt` — der aber auch dann eintritt, wenn ein ANDERER Schluessel
 * beobachtet wird (Seitenwechsel). Die Blockade fiel damit, ohne dass die
 * Marker der betroffenen Seite je neu geladen worden waeren. Wer auf einen
 * bestimmten Abruf wartet, muss sagen koennen, auf WELCHEN — dafuer dieser
 * Schluessel, aus EINER Quelle fuer Hook und Ansicht.
 */
export function cleanupKeywordsQueryKey(query: CleanupKeywordsQuery = {}): readonly unknown[] {
  return ['cleanupKeywords', query]
}

export function useCleanupKeywords(query: CleanupKeywordsQuery = {}) {
  const params = new URLSearchParams()
  if (query.daysThreshold) params.set('daysThreshold', String(query.daysThreshold))
  if (query.limit) params.set('limit', String(query.limit))
  if (query.offset) params.set('offset', String(query.offset))

  const qs = params.toString()
  return useQuery({
    queryKey: cleanupKeywordsQueryKey(query),
    // CLEANUP-SICHT: `openReconciles` haengt additiv an derselben Route — die
    // Ansicht laedt sie ohnehin, und ein ungeklaerter Fall gehoert neben die
    // Kandidatenliste, nicht in einen zweiten Reiter.
    queryFn: () => api.get<CleanupListResponse>(`/promotions/cleanup/${qs ? `?${qs}` : ''}`),
  })
}

export function useExecuteCleanup() {
  const qc = useQueryClient()
  // Gleiche Klammer wie `useBatchKeywordState` (keywords.ts): der Hook wird IN
  // `CleanupView` aufgerufen und teilt deren Lebenszyklus. Nur solange die
  // Ansicht lebt, kann sie den 502 selbst darstellen — sonst darf der globale
  // Toast NICHT unterdrueckt werden. `true` steht im Effekt-RUMPF, weil Refs das
  // simulierte Unmount/Remount des StrictMode-Dev-Builds ueberleben.
  const isMounted = useRef(false)
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  return useMutation<CleanupExecuteResult, unknown, number[]>({
    mutationFn: async (keywordIds) => {
      try {
        return await api.post<CleanupExecuteResult>('/promotions/cleanup/execute/', { keywordIds })
      } catch (err) {
        // Muss HIER stehen und nicht in `onError`: der globale
        // MutationCache-Handler (main.tsx) laeuft VOR jedem `onError`.
        // `CleanupView` zeigt den Fall selbst an — aber nur, wenn es sie noch
        // gibt. Ein verschluckter Hinweis waere schlimmer als ein doppelter.
        if (isMounted.current) markReconcileHandledByList(err)
        throw err
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cleanupKeywords'] })
    },
    // CLEANUP-SICHT: Der 502-Fall ist KEIN reiner Fehlschlag — Teile des Batches
    // koennen bei Amazon gelandet sein, und der Server hat dazu durable Marker
    // geschrieben. Deshalb auch hier neu laden (Muster keywords.ts:149).
    onError: (err) => {
      if (reconcileRequiredCount(err) > 0) qc.invalidateQueries({ queryKey: ['cleanupKeywords'] })
    },
  })
}

/**
 * CLEANUP-SICHT: Quittierung eines Pruefmarkers.
 *
 * Schreibt eine FESTSTELLUNG des Operators fest — kein Amazon-Abruf, keine
 * Verifikation (Koordinator-Auflage 2026-08-04). Die Route liegt hinter
 * `requireWriteMode` und antwortet im Demo-Modus 423; die Ansicht sperrt die
 * Buttons dort, statt sie anzubieten.
 */
export function useResolveCleanupReconcile() {
  const qc = useQueryClient()
  const isMounted = useRef(false)
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  return useMutation<CleanupResolveResult, unknown, { id: number; outcome: CleanupReconcileOutcome }>({
    mutationFn: async ({ id, outcome }) => {
      try {
        return await api.post<CleanupResolveResult>(`/promotions/cleanup/reconcile/${id}/resolve`, { outcome })
      } catch (err) {
        // 404 ist hier der NORMALFALL, kein Fehler: zwischen Laden und Klicken
        // kann jemand anderes denselben Marker quittiert haben (die Route gibt
        // 404 fuer „unbekannt" UND „nicht mehr offen"). Die Ansicht meldet das
        // sachlich als „war bereits erledigt" — ein roter Fehler-Toast
        // daneben wuerde einen Zwischenfall behaupten, den es nicht gab.
        // Dieselbe Vorbedingung wie bei `markReconcileHandledByList`: nur
        // unterdruecken, wenn die Ansicht die Meldung auch zeigen kann.
        //
        // 409 ebenso (Whole-Branch-Fix I1): der lokale Zustand widerspricht der
        // Feststellung. Die Ansicht zeigt die Begruendung BLEIBEND an — ein
        // Toast waere nach Sekunden weg, waehrend der Widerspruch bleibt und
        // geklaert werden muss.
        if (isMounted.current && err instanceof ApiError && (err.status === 404 || err.status === 409)) {
          err.toasted = true
        }
        throw err
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cleanupKeywords'] })
    },
    onError: (err) => {
      // Marker weg heisst: die geladene Liste ist veraltet. Ohne Invalidierung
      // zeigte sie weiter einen Eintrag, den es nicht mehr gibt.
      if (err instanceof ApiError && err.status === 404) qc.invalidateQueries({ queryKey: ['cleanupKeywords'] })
    },
  })
}
